import { useState } from 'react'
import type { MissionDto } from '@/api/hooks'
import { useCancelMission } from '@/api/hooks'
import { isMissionCancellable } from '@/lib/missionCancel'
import { RumIcon } from '@/components/game/GameIcons'
import toast from 'react-hot-toast'

/**
 * « Annuler la mission » — only offered while the fleet is still outbound
 * (`isMissionCancellable`, mirrors the server's `now < midpoint` rule). The crew's
 * rhum ration is NOT refunded (drunk at sea) and the ships take real time to sail
 * home (no instant return), so the action needs an explicit confirm step spelling
 * out the trade-off — never a silent one-click cancel, never `window.confirm`.
 * What the hold carried DOES come back, at landing: see
 * `ReturnCancelledMissionMessageHandler`.
 */
export function CancelMissionButton({ mission }: { mission: MissionDto }) {
  const cancelMission = useCancelMission()
  const [confirming, setConfirming] = useState(false)

  if (!isMissionCancellable(mission)) return null

  const confirm = () => {
    cancelMission.mutate(mission.id, {
      onSuccess: () => setConfirming(false),
      onError: (err) => {
        setConfirming(false)
        toast.error(err.message || 'Annulation impossible')
      },
    })
  }

  if (confirming) {
    return (
      <div className="flex flex-col items-end gap-1.5 rounded-lg border border-rum-500/30 bg-ocean-900/70 p-2 max-w-[220px]">
        <p className="text-[11px] text-rum-300 text-right leading-snug">
          La ration de <RumIcon className="w-3 h-3 inline -mt-0.5" /> déjà bue en mer ne sera pas
          remboursée, et les navires mettront du temps à rentrer (pas de retour instantané). Ce que la
          cale emportait redescend à quai au retour.
        </p>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setConfirming(false)}
            disabled={cancelMission.isPending}
            className="text-[11px] px-2 py-1 rounded-md border border-sand-500/25 text-sand-300 hover:bg-ocean-700 transition-colors disabled:opacity-40"
          >
            Retour
          </button>
          <button
            onClick={confirm}
            disabled={cancelMission.isPending}
            className="text-[11px] px-2 py-1 rounded-md bg-rum-500/80 text-ocean-50 font-semibold hover:bg-rum-500 transition-colors disabled:opacity-40"
          >
            {cancelMission.isPending ? 'Annulation…' : 'Confirmer l’annulation'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      aria-label="Annuler la mission"
      title="Annuler la mission (rappelle la flotte, ration de rhum non remboursée)"
      className="w-7 h-7 rounded-full border border-rum-500/30 bg-ocean-900/60 text-rum-300 hover:bg-ocean-700 hover:text-rum-200 transition-colors flex items-center justify-center"
    >
      <CancelIcon className="w-3.5 h-3.5" />
    </button>
  )
}

function CancelIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5l5 5M14.5 9.5l-5 5" />
    </svg>
  )
}
